import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TopRightAuth from "../components/TopRightAuth";
import { isEmbedPath } from "../context/EmbedContext";
import { GraduationCap, Wrench, ExternalLink, Play, FileText, Headphones, ArrowLeft, Search, Globe, Monitor, Terminal, Puzzle, Eye, PenTool, BookOpen, Landmark, Gavel, ShieldCheck, Link as LinkIcon } from "lucide-react";
import trainingData from "../data/trainingItems.json";
import toolData from "../data/toolItems.json";
import regulationsData from "../data/regulations.json";
import "../pages/News.css";

/* ── YouTube thumbnail helpers ── */
function extractYouTubeId(url) {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    return match ? match[1] : null;
}

function getYouTubeThumbnail(url) {
    const id = extractYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

/* ── Themed gradient maps ── */
const TRAINING_GRADIENTS = {
    Video: { bg: 'linear-gradient(135deg, #0d7377 0%, #14919b 50%, #0d7377 100%)', icon: Play, iconColor: '#fff' },
    Audio: { bg: 'linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #6366f1 100%)', icon: Headphones, iconColor: '#fff' },
    Article: { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)', icon: FileText, iconColor: '#fff' },
    Document: { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #10b981 100%)', icon: BookOpen, iconColor: '#fff' },
    default: { bg: 'linear-gradient(135deg, #64748b 0%, #94a3b8 50%, #64748b 100%)', icon: FileText, iconColor: '#fff' },
};

const TOOL_GRADIENTS = {
    'Browser Extension': { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 50%, #8b5cf6 100%)', icon: Puzzle },
    'Screen Reader': { bg: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 50%, #0ea5e9 100%)', icon: Eye },
    'CLI / CI': { bg: 'linear-gradient(135deg, #22c55e 0%, #4ade80 50%, #22c55e 100%)', icon: Terminal },
    'PDF Tool': { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 50%, #ef4444 100%)', icon: FileText },
    'Web-based tool': { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)', icon: Globe },
    'Developer tool': { bg: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 50%, #14b8a6 100%)', icon: Monitor },
    'Design plugin': { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 50%, #ec4899 100%)', icon: PenTool },
    'Screen magnifier': { bg: 'linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #6366f1 100%)', icon: Search },
    default: { bg: 'linear-gradient(135deg, #0d7377 0%, #14919b 50%, #0d7377 100%)', icon: Wrench },
};

function getTrainingTheme(mediaType) {
    if (!mediaType) return TRAINING_GRADIENTS.default;
    const lower = mediaType.toLowerCase();
    if (lower.includes('video')) return TRAINING_GRADIENTS.Video;
    if (lower.includes('audio') || lower.includes('mp3')) return TRAINING_GRADIENTS.Audio;
    if (lower.includes('article')) return TRAINING_GRADIENTS.Article;
    if (lower.includes('document') || lower.includes('pdf')) return TRAINING_GRADIENTS.Document;
    return TRAINING_GRADIENTS.default;
}

function getToolTheme(toolType) {
    if (!toolType) return TOOL_GRADIENTS.default;
    const lower = toolType.toLowerCase();
    if (lower.includes('browser extension')) return TOOL_GRADIENTS['Browser Extension'];
    if (lower.includes('screen reader')) return TOOL_GRADIENTS['Screen Reader'];
    if (lower.includes('cli') || lower.includes('ci ')) return TOOL_GRADIENTS['CLI / CI'];
    if (lower.includes('pdf')) return TOOL_GRADIENTS['PDF Tool'];
    if (lower.includes('web-based') || lower.includes('web‑based')) return TOOL_GRADIENTS['Web-based tool'];
    if (lower.includes('developer') || lower.includes('devtools') || lower.includes('chrome')) return TOOL_GRADIENTS['Developer tool'];
    if (lower.includes('design') || lower.includes('figma')) return TOOL_GRADIENTS['Design plugin'];
    if (lower.includes('magnif')) return TOOL_GRADIENTS['Screen magnifier'];
    return TOOL_GRADIENTS.default;
}



function extractTags(tagString, separator) {
    if (!tagString) return [];
    return tagString.split(separator).map(t => t.trim()).filter(t => t.length > 0);
}

function TrainingCard({ item }) {
    const mediaType = item["Media Type"] || "Resource";
    const title = item["Abstract"] || item["Title"] || "Untitled Resource";
    let url = item["Webpage / URL"] || item["Source URL/Repository"] || "";
    if (url && !url.startsWith("http")) url = "https://" + url;
    const tags = extractTags(item["Keywords/Tags"], ";").slice(0, 4);
    const author = item["Author/Organization"] || "Unknown Author";
    const date = item["Date Published"] || item["Last Updated"] || "";
    const ytThumb = getYouTubeThumbnail(url);
    const theme = getTrainingTheme(mediaType);
    const ThemeIcon = theme.icon;

    let description = item["Detailed Description"] || "";
    if (description.length > 150) {
        description = description.substring(0, 150) + "...";
    }

    return (
        <div className="news-card fade-in-up">
            {ytThumb ? (
                <div className="news-card-image" style={{ position: 'relative', overflow: 'hidden' }}>
                    <img
                        src={ytThumb}
                        alt={title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div style={{ display: 'none', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, background: theme.bg, alignItems: 'center', justifyContent: 'center' }}>
                        <ThemeIcon size={48} color={theme.iconColor} style={{ opacity: 0.9 }} aria-hidden="true" focusable="false" />
                    </div>
                    {/* Play overlay for YouTube */}
                    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={`Play video: ${title}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)', textDecoration: 'none' }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                            <Play size={24} color="#fff" fill="#fff" style={{ marginLeft: '4px' }} aria-hidden="true" focusable="false" />
                        </div>
                    </a>
                </div>
            ) : (
                <div className="news-card-image" style={{ background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <ThemeIcon size={48} color={theme.iconColor} style={{ opacity: 0.9 }} aria-hidden="true" focusable="false" />
                    <span style={{ position: 'absolute', bottom: 10, right: 14, fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px' }}>{mediaType}</span>
                </div>
            )}
            <div className="news-card-content">
                <div className="news-card-meta">
                    <span className="news-card-category">{mediaType}</span>
                    <span>{date}</span>
                </div>

                <h3 className="news-card-title">{title}</h3>

                {tags.length > 0 && (
                    <div className="news-badges">
                        {tags.map((tag, i) => (
                            <span key={i} className="news-badge">{tag}</span>
                        ))}
                    </div>
                )}

                <p className="news-card-description">{description}</p>

                <div className="news-card-footer mt-auto">
                    <span className="news-card-source truncate" style={{ maxWidth: '200px' }} title={author}>{author}</span>
                    {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="news-card-link">
                            Open <ExternalLink size={14} aria-hidden="true" focusable="false" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function ToolCard({ item }) {
    const mediaType = item["Type of tool"] || "Tool";
    const title = item["Name"] || "Untitled Tool";
    let url = item["Webpage/URL"] || "";
    if (url && !url.startsWith("http")) url = "https://" + url;
    const tags = extractTags(item["Keywords/Tags"], ",").slice(0, 4);
    const author = item["Tool manufacturer"] || "Unknown Maker";
    const pricing = item["Pricing model"] || "Unknown Price";
    const theme = getToolTheme(mediaType);
    const ThemeIcon = theme.icon;

    let description = item["Tool description"] || "";
    if (description.length > 150) {
        description = description.substring(0, 150) + "...";
    }

    return (
        <div className="news-card fade-in-up">
            <div className="news-card-image" style={{ background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <ThemeIcon size={48} color="#fff" style={{ opacity: 0.9 }} aria-hidden="true" focusable="false" />
                <span style={{ position: 'absolute', bottom: 10, right: 14, fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {mediaType.length > 25 ? mediaType.substring(0, 25) + '…' : mediaType}
                </span>
            </div>
            <div className="news-card-content">
                <div className="news-card-meta justify-between w-full flex">
                    <span className="news-card-category" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }} title={mediaType}>{mediaType}</span>
                </div>

                <h3 className="news-card-title">{title}</h3>

                {pricing && pricing !== "Unknown Price" && (
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginBottom: '12px', fontWeight: '500', lineHeight: '1.4' }}>
                        {pricing}
                    </div>
                )}

                {tags.length > 0 && (
                    <div className="news-badges">
                        {tags.map((tag, i) => (
                            <span key={i} className="news-badge">{tag}</span>
                        ))}
                    </div>
                )}

                <p className="news-card-description">{description}</p>

                <div className="news-card-footer mt-auto">
                    <span className="news-card-source truncate" style={{ maxWidth: '200px' }} title={author}>{author}</span>
                    {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="news-card-link">
                            Open <ExternalLink size={14} aria-hidden="true" focusable="false" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function RegulationCard({ item }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const title = item["Title"] || "Untitled Regulation";
    const rawAbstract = item["Abstract"] || item["Description"] || "";
    const authorityType = item["Authority_Type"] || "Regulation";
    const coveredEntities = item["Covered_Entities"] || "State & Local Governments";
    const complianceCategory = item["Compliance_Category"] || "Legal Requirement";
    let url = item["Source_URL"] || "";
    if (url && !url.startsWith("http")) url = "https://" + url;

    let shortUrl = url;
    try {
        if (url) shortUrl = new URL(url).hostname;
    } catch (e) { }

    const maxLength = 150;
    const shouldTruncate = rawAbstract.length > maxLength;
    const abstract = shouldTruncate && !isExpanded
        ? rawAbstract.substring(0, maxLength) + "..."
        : rawAbstract;

    return (
        <div className="news-card fade-in-up" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Landmark size={20} aria-hidden="true" focusable="false" />
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>Regulations & Laws</span>
                </div>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1, backgroundColor: 'var(--color-surface)' }}>
                <h3 className="news-card-title" style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 12px 0', color: 'var(--color-text)' }}>{title}</h3>

                <div style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: '0 0 20px 0', lineHeight: 1.6, flex: 1, minHeight: '68px' }}>
                    {abstract}
                    {shouldTruncate && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{ background: 'none', border: 'none', color: 'var(--color-link)', cursor: 'pointer', fontWeight: 600, padding: '0 0 0 6px', fontSize: '14px' }}
                        >
                            {isExpanded ? "Show Less" : "Read More"}
                        </button>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', padding: '16px 0', marginTop: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', fontSize: '14px' }}>
                        <span style={{ width: '150px', flexShrink: 0, color: 'var(--color-text-muted)' }}>Authority Type:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text)', fontWeight: 500 }}>
                            <Gavel size={16} color="var(--color-text-muted)" aria-hidden="true" focusable="false" />
                            <span>{authorityType}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', fontSize: '14px' }}>
                        <span style={{ width: '150px', flexShrink: 0, color: 'var(--color-text-muted)' }}>Covered Entities:</span>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--color-text)', fontWeight: 500 }}>
                            <Landmark size={16} color="var(--color-text-muted)" style={{ marginTop: '2px' }} aria-hidden="true" focusable="false" />
                            <span style={{ lineHeight: 1.4 }}>{coveredEntities}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <span style={{ width: '150px', flexShrink: 0, color: 'var(--color-text-muted)' }}>Compliance Category:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text)', fontWeight: 500 }}>
                            <ShieldCheck size={16} color="var(--color-text-muted)" aria-hidden="true" focusable="false" />
                            <span>{complianceCategory}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <span style={{ width: '150px', flexShrink: 0, color: 'var(--color-text-muted)' }}>Source:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-link)', fontWeight: 600 }}>
                            <LinkIcon size={16} aria-hidden="true" focusable="false" />
                            {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.target.style.color = 'var(--color-link-hover)'; }} onMouseLeave={(e) => { e.target.style.color = 'var(--color-link)'; }}>Read More: {shortUrl}</a> : "N/A"}
                        </div>
                    </div>
                </div>

                {url && (
                    <div style={{ paddingTop: '20px', display: 'flex', justifyContent: 'center' }}>
                        <a href={url || "#"} target={url ? "_blank" : "_self"} rel="noopener noreferrer" style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '10px 24px', borderRadius: '20px', textDecoration: 'none', fontWeight: 600, fontSize: '14px', textAlign: 'center', width: '200px', transition: 'all 0.2s ease', display: 'inline-block', boxShadow: 'var(--data-card-shadow)' }} onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--color-accent)'; e.target.style.transform = 'translateY(-2px)' }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'var(--color-primary)'; e.target.style.transform = 'translateY(0)' }}>
                            View Details
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

function TrainingResources() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("Training"); // "Training", "Tools", or "Regulations"
    const [activeTrainingFilter, setActiveTrainingFilter] = useState("All");
    const [activeToolFilter, setActiveToolFilter] = useState("All");
    const [activeRegulationFilter, setActiveRegulationFilter] = useState("All");

    const [searchQuery, setSearchQuery] = useState("");

    // Dynamic categories based on available Media Types / Tool Types
    const trainingTypes = [...new Set(trainingData.map(item => item["Media Type"]).filter(Boolean))];
    const trainingCategories = ["All", ...trainingTypes].slice(0, 7);

    // Some tool types are very long, we can map to simplified or just show popular ones
    const toolTypesUnfiltered = toolData.map(item => item["Type of tool"]).filter(Boolean);
    const toolTypesSet = [...new Set(toolTypesUnfiltered)].map(t => {
        if (t.includes("Browser extension")) return "Browser Extension";
        if (t.toLowerCase().includes("screen reader")) return "Screen Reader";
        if (t.includes("CLI")) return "CLI / CI";
        if (t.includes("PDF")) return "PDF Tool";
        return t;
    });
    const popularToolTypes = [...new Set(toolTypesSet)].slice(0, 6);
    const toolCategories = ["All", ...popularToolTypes];

    const regulationTypes = [...new Set(regulationsData.map(item => item["Authority_Type"]).filter(Boolean))];
    const regulationCategories = ["All", ...regulationTypes];

    const matchSearch = (item, type) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        if (type === "training") {
            const title = (item["Abstract"] || item["Title"] || "").toLowerCase();
            const desc = (item["Detailed Description"] || "").toLowerCase();
            const tags = (item["Keywords/Tags"] || "").toLowerCase();
            return title.includes(q) || desc.includes(q) || tags.includes(q);
        } else if (type === "tools") {
            const title = (item["Name"] || "").toLowerCase();
            const desc = (item["Tool description"] || "").toLowerCase();
            const tags = (item["Keywords/Tags"] || "").toLowerCase();
            return title.includes(q) || desc.includes(q) || tags.includes(q);
        } else if (type === "regulations") {
            const title = (item["Title"] || "").toLowerCase();
            const desc = (item["Abstract"] || item["Description"] || "").toLowerCase();
            return title.includes(q) || desc.includes(q);
        }
        return true;
    };

    const filteredTraining = trainingData.filter(item => {
        if (activeTrainingFilter !== "All" && item["Media Type"] !== activeTrainingFilter) return false;
        return matchSearch(item, "training");
    });

    const filteredTools = toolData.filter(item => {
        const t = item["Type of tool"];
        if (activeToolFilter !== "All") {
            if (!t) return false;
            if (activeToolFilter === "Browser Extension" && !t.includes("Browser extension")) return false;
            else if (activeToolFilter === "Screen Reader" && !t.toLowerCase().includes("screen reader")) return false;
            else if (activeToolFilter === "CLI / CI" && !t.includes("CLI")) return false;
            else if (activeToolFilter === "PDF Tool" && !t.includes("PDF")) return false;
            else if (activeToolFilter !== "Browser Extension" && activeToolFilter !== "Screen Reader" && activeToolFilter !== "CLI / CI" && activeToolFilter !== "PDF Tool" && t !== activeToolFilter) return false;
        }
        return matchSearch(item, "tools");
    });

    const filteredRegulations = regulationsData.filter(item => {
        if (activeRegulationFilter !== "All" && item["Authority_Type"] !== activeRegulationFilter) return false;
        return matchSearch(item, "regulations");
    });

    return (
        <div className="news-page fade-in-up" style={{ animationDelay: '0.1s', position: 'relative' }}>
            {!isEmbedPath() && <TopRightAuth />}

            <button
                onClick={() => navigate("/")}
                className="chat-back-button fade-in-up"
                title="Back to Home"
                aria-label="Back to home"
            >
                <ArrowLeft size={16} aria-hidden="true" focusable="false" />
                <span>Back</span>
            </button>

            <div className="news-container">
                <div className="news-header">
                    <h1 className="news-title">
                        <GraduationCap size={36} style={{ color: 'var(--brand-emphasis-text)' }} aria-hidden="true" focusable="false" />
                        Training <span className="brand-primary-text">Resources</span>
                    </h1>
                    <p className="news-subtitle">A catalog of training materials, software tools, and best practices to master accessibility.</p>
                </div>

                <div className="sr-only" role="status" aria-live="polite">
                    {activeTab === "Training" && `${filteredTraining.length} training ${filteredTraining.length === 1 ? "resource" : "resources"} shown`}
                    {activeTab === "Tools" && `${filteredTools.length} accessibility ${filteredTools.length === 1 ? "tool" : "tools"} shown`}
                    {activeTab === "Regulations" && `${filteredRegulations.length} ${filteredRegulations.length === 1 ? "regulation" : "regulations"} shown`}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px', borderBottom: '1px solid var(--color-border)', paddingBottom: '16px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setActiveTab("Training")}
                                style={{
                                    padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s', cursor: 'pointer',
                                    backgroundColor: activeTab === "Training" ? 'var(--color-primary)' : 'transparent',
                                    color: activeTab === "Training" ? '#fff' : 'var(--color-text)',
                                    border: activeTab === "Training" ? '1px solid var(--color-primary)' : '1px solid var(--color-border)'
                                }}
                            >
                                Training Catalog
                            </button>
                            <button
                                onClick={() => setActiveTab("Tools")}
                                style={{
                                    padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s', cursor: 'pointer',
                                    backgroundColor: activeTab === "Tools" ? 'var(--color-primary)' : 'transparent',
                                    color: activeTab === "Tools" ? '#fff' : 'var(--color-text)',
                                    border: activeTab === "Tools" ? '1px solid var(--color-primary)' : '1px solid var(--color-border)'
                                }}
                            >
                                Accessibility Tools
                            </button>
                            <button
                                onClick={() => setActiveTab("Regulations")}
                                style={{
                                    padding: '8px 16px', borderRadius: '6px', fontWeight: '500', transition: 'all 0.2s', cursor: 'pointer',
                                    backgroundColor: activeTab === "Regulations" ? 'var(--color-primary)' : 'transparent',
                                    color: activeTab === "Regulations" ? '#fff' : 'var(--color-text)',
                                    border: activeTab === "Regulations" ? '1px solid var(--color-primary)' : '1px solid var(--color-border)'
                                }}
                            >
                                Regulations & Laws
                            </button>
                        </div>

                        <div className="news-search-container">
                            <Search size={18} className="news-search-icon" aria-hidden="true" focusable="false" />
                            <input
                                type="text"
                                aria-label={`Search ${activeTab.toLowerCase()}`}
                                placeholder={`Search ${activeTab.toLowerCase()}...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="news-search-input"
                            />
                        </div>
                    </div>
                </div>

                {activeTab === "Training" ? (
                    <>
                        <div className="news-filters">
                            {trainingCategories.map(category => (
                                <button
                                    key={category}
                                    className={`news-filter-btn ${activeTrainingFilter === category ? "active" : ""}`}
                                    onClick={() => setActiveTrainingFilter(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        <div className="news-grid">
                            {filteredTraining.length > 0 ? (
                                filteredTraining.map((item, index) => (
                                    <TrainingCard key={`train-${index}`} item={item} />
                                ))
                            ) : (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                                    No training resources found for this category.
                                </div>
                            )}
                        </div>
                    </>
                ) : activeTab === "Tools" ? (
                    <>
                        <div className="news-filters">
                            {toolCategories.map(category => (
                                <button
                                    key={category}
                                    className={`news-filter-btn ${activeToolFilter === category ? "active" : ""}`}
                                    onClick={() => setActiveToolFilter(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        <div className="news-grid">
                            {filteredTools.length > 0 ? (
                                filteredTools.map((item, index) => (
                                    <ToolCard key={`tool-${index}`} item={item} />
                                ))
                            ) : (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                                    No tools found for this category.
                                </div>
                            )}
                        </div>
                    </>
                ) : activeTab === "Regulations" ? (
                    <>
                        <div className="news-filters">
                            {regulationCategories.map(category => (
                                <button
                                    key={category}
                                    className={`news-filter-btn ${activeRegulationFilter === category ? "active" : ""}`}
                                    onClick={() => setActiveRegulationFilter(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        <div className="news-grid">
                            {filteredRegulations.length > 0 ? (
                                filteredRegulations.map((item, index) => (
                                    <RegulationCard key={`reg-${item["Authority_ID"] || index}`} item={item} />
                                ))
                            ) : (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                                    No regulations found matching your criteria.
                                </div>
                            )}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default TrainingResources;
